import { create } from "zustand";
import { readSse } from "../../chat/sse";
import { getCsrfToken } from "../../state/csrf";
import { ActiveParcel, activeParcelBody } from "./activeParcel";
import { applyChatEvent, emptyChatStream, failChatStream, stopChatStream } from "./chatEvents";
import { ChatSessionSummary, ReportProposal, SkillDefinition, WorkspaceMessage } from "./types";

const STREAM_FAILED = "Er ging iets mis tijdens chatten. Probeer het later opnieuw.";
const SKILL_FAILED = "Starten mislukt; probeer het opnieuw.";

// Wat starten oplevert: gelukt, of de redenen die het formulier toont.
export type SkillOutcome =
  { ok: true; message: WorkspaceMessage } | { ok: false; errors: string[] };

const emptyMessage = (role: "user" | "assistant", content: string): WorkspaceMessage => ({
  role,
  content,
  finished: true,
  filters: [],
  researchProposals: [],
  reportProposals: [],
  skillRuns: [],
});

const refusal = async (response: Response): Promise<string[]> => {
  const body = await response.json().catch(() => ({}));
  return body.errors?.length ? body.errors : [body.detail || SKILL_FAILED];
};

// Buiten de store: een AbortController hoort niet in renderbare state.
let controller: AbortController | null = null;

const json = (method: string, body?: any): RequestInit => ({
  method,
  headers: { "Content-Type": "application/json", "X-CSRFToken": getCsrfToken() },
  ...(body ? { body: JSON.stringify(body) } : {}),
});

interface WorkspaceState {
  workspaceId?: number;
  sessions: ChatSessionSummary[];
  activeSessionId?: number;
  // Door de server aangemaakt tijdens het streamen; de view zet hem in de URL
  // en wist hem daarna weer.
  newSessionId?: number;
  messages: WorkspaceMessage[];
  isStreaming: boolean;
  error?: string;
  openWorkspace: (workspaceId: number) => void;
  loadSessions: () => Promise<ChatSessionSummary[]>;
  loadSession: (sessionId?: number) => Promise<boolean>;
  newSession: () => void;
  clearNewSession: () => void;
  sendMessage: (content: string, active?: ActiveParcel) => Promise<void>;
  stop: () => void;
  renameSession: (sessionId: number, title: string) => Promise<void>;
  deleteSession: (sessionId: number) => Promise<void>;
  // Het rapportvoorstel uit een antwoord laten omzetten in een PDF-projectbestand.
  createReportPdf: (reportId: number) => Promise<boolean>;
  // De status van een voorstel opnieuw ophalen (polling zolang de PDF gemaakt wordt).
  refreshReport: (reportId: number) => Promise<ReportProposal | undefined>;
  applyReport: (updated: ReportProposal) => void;
  skills: SkillDefinition[];
  loadSkills: () => Promise<void>;
  // Een skill uit het menu: een gemarkeerde vraag en een antwoord, zonder model.
  runSkill: (name: string, values: Record<string, any>) => Promise<SkillOutcome>;
  // Een aangeboden skill starten met de waarden uit zijn formulier.
  startSkillRun: (runId: number, values: Record<string, any>) => Promise<SkillOutcome>;
  // Een eigen skill: een beurt van het model, via de chatstroom. `onAccepted`
  // vuurt zodra de server de waarden aanneemt, zodat het formulier dicht kan.
  startSkillTurn: (
    start: { skill: string } | { skill_run_id: number },
    values: Record<string, any>,
    label: string,
    onAccepted?: () => void
  ) => Promise<string[] | undefined>;
  // Een betaalde actie bevestigen of annuleren; geeft de redenen bij een weigering.
  confirmAction: (actionRunId: number) => Promise<string[] | undefined>;
  cancelAction: (actionRunId: number) => Promise<string[] | undefined>;
}

export const useWorkspace = create<WorkspaceState>((set, get) => {
  const streamTurn = async (
    body: Record<string, any>,
    question: string,
    onAccepted?: () => void
  ): Promise<string[] | undefined> => {
    const { workspaceId, activeSessionId, isStreaming } = get();
    if (!workspaceId || isStreaming) return [SKILL_FAILED];

    set((state) => ({
      messages: [...state.messages, emptyMessage("user", question), emptyMessage("assistant", "")],
      isStreaming: true,
      error: undefined,
    }));

    // De stroom zelf staat in chatEvents.ts; hier blijft alleen over wat een
    // store doet: hem in het laatste bericht zetten (altijd de assistent-rij die
    // we net toevoegden) en de bijwerkingen eromheen.
    let stream = emptyChatStream();
    const syncAnswer = () =>
      set((state) => {
        const messages = [...state.messages];
        const idx = messages.length - 1;
        if (idx < 0 || messages[idx].role !== "assistant") return {};
        messages[idx] = {
          ...messages[idx],
          id: stream.assistantMessageId ?? messages[idx].id,
          content: stream.content,
          finished: stream.finished,
          filters: stream.filters,
          researchProposals: stream.researchProposals,
          reportProposals: stream.reportProposals,
          skillRuns: stream.skillRuns,
        };
        return { messages };
      });

    controller = new AbortController();
    try {
      const response = await fetch(`/api/workspaces/${workspaceId}/chat/message`, {
        // Het aangeklikte kavel reist mee in `body`: een aandachtshint voor het
        // model, die de server controleert en per bericht bewaart.
        ...json("POST", { ...body, session_id: activeSessionId }),
        signal: controller.signal,
      });
      // Een skill met verkeerde waarden weigert de server vóór er iets streamt;
      // dan is er geen vraag en geen antwoord, en toont het formulier waarom.
      if (response.status === 400 && body.values) {
        set((state) => ({ messages: state.messages.slice(0, -2) }));
        return await refusal(response);
      }
      onAccepted?.();

      // De SSE-events komen uit een plain Django view en zijn snake_case; de
      // ninja-endpoints leveren camelCase. `applyChatEvent` is de enige plek
      // waar dat verschil bestaat -- daarbuiten praat alles camelCase.
      await readSse(response, (data) => {
        const previous = stream;
        stream = applyChatEvent(stream, data);
        if (stream === previous) return; // onbekend event: niets aan de hand

        if (stream.sessionId && stream.sessionId !== previous.sessionId) {
          set({
            activeSessionId: stream.sessionId,
            // Alleen als de server hem net heeft aangemaakt: de view zet hem dan
            // in de URL. Sturen op "store en URL lopen uiteen" zou ook vuren als
            // je zelf een nieuwe chat begint, en je dan terugkaatsen.
            newSessionId: activeSessionId ? undefined : stream.sessionId,
          });
          syncAnswer();
          get().loadSessions();
          return;
        }
        if (stream.deleted) {
          set((state) => ({ messages: state.messages.slice(0, -1) }));
          return;
        }
        syncAnswer();
      });
    } catch (e: any) {
      if (e?.name === "AbortError") {
        // De server bewaart het gedeeltelijke antwoord met finished=False.
        stream = stopChatStream(stream);
        syncAnswer();
      } else {
        stream = failChatStream(stream, STREAM_FAILED);
        syncAnswer();
        set({ error: STREAM_FAILED });
      }
    } finally {
      controller = null;
      set({ isStreaming: false });
      get().loadSessions();
    }
    return undefined;
  };

  // Een actiekaart bevestigen of annuleren; het antwoord komt heel terug.
  const actOn = async (actionRunId: number, verb: "confirm" | "cancel") => {
    const { workspaceId } = get();
    if (!workspaceId) return [SKILL_FAILED];
    const response = await fetch(
      `/api/workspaces/${workspaceId}/chat/action-runs/${actionRunId}/${verb}`,
      json("POST")
    );
    if (!response.ok) return refusal(response);
    const message: WorkspaceMessage = await response.json();
    set((state) => ({
      messages: state.messages.map((item) => (item.id === message.id ? message : item)),
    }));
    return undefined;
  };

  return {
    sessions: [],
    messages: [],
    isStreaming: false,
    skills: [],

    openWorkspace: (workspaceId: number) => {
      if (get().workspaceId === workspaceId) return;
      set({
        workspaceId,
        sessions: [],
        messages: [],
        skills: [],
        activeSessionId: undefined,
        error: undefined,
      });
    },

    loadSkills: async () => {
      const { workspaceId } = get();
      if (!workspaceId) return;
      const response = await fetch(`/api/workspaces/${workspaceId}/chat/skills`);
      if (response.ok) set({ skills: await response.json() });
    },

    runSkill: async (name, values) => {
      const { workspaceId, activeSessionId } = get();
      if (!workspaceId) return { ok: false, errors: [SKILL_FAILED] };
      const response = await fetch(
        `/api/workspaces/${workspaceId}/chat/skills/${name}/run`,
        json("POST", { values, session_id: activeSessionId })
      );
      if (!response.ok) return { ok: false, errors: await refusal(response) };
      const result: { sessionId: number; messages: WorkspaceMessage[] } = await response.json();
      set((state) => ({
        messages: [...state.messages, ...result.messages],
        activeSessionId: result.sessionId,
        // Net aangemaakt: de view zet hem in de URL, net als bij een eerste vraag.
        newSessionId: activeSessionId ? undefined : result.sessionId,
      }));
      get().loadSessions();
      return { ok: true, message: result.messages[result.messages.length - 1] };
    },

    startSkillRun: async (runId, values) => {
      const { workspaceId } = get();
      if (!workspaceId) return { ok: false, errors: [SKILL_FAILED] };
      const response = await fetch(
        `/api/workspaces/${workspaceId}/chat/skill-runs/${runId}/start`,
        json("POST", { values })
      );
      if (!response.ok) return { ok: false, errors: await refusal(response) };
      const message: WorkspaceMessage = await response.json();
      // Het antwoord komt heel terug: de kaart toont dan zijn record.
      set((state) => ({
        messages: state.messages.map((item) => (item.id === message.id ? message : item)),
      }));
      return { ok: true, message };
    },

    loadSessions: async () => {
      const { workspaceId } = get();
      if (!workspaceId) return [];
      const response = await fetch(`/api/workspaces/${workspaceId}/chat/sessions`);
      if (!response.ok) return [];
      const sessions: ChatSessionSummary[] = await response.json();
      set({ sessions });
      return sessions;
    },

    // Returns false als de sessie niet bestaat of niet bij dit project hoort.
    loadSession: async (sessionId?: number) => {
      const { workspaceId } = get();
      if (!workspaceId || !sessionId) {
        set({ activeSessionId: undefined, messages: [] });
        return true;
      }
      const response = await fetch(`/api/workspaces/${workspaceId}/chat/sessions/${sessionId}`);
      if (!response.ok) {
        set({ activeSessionId: undefined, messages: [] });
        return false;
      }
      const detail = await response.json();
      set({ activeSessionId: detail.id, messages: detail.messages, error: undefined });
      return true;
    },

    newSession: () =>
      set({ activeSessionId: undefined, messages: [], error: undefined, newSessionId: undefined }),
    clearNewSession: () => set({ newSessionId: undefined }),

    sendMessage: async (content: string, active?: ActiveParcel) => {
      await streamTurn({ content, ...activeParcelBody(active) }, content);
    },

    startSkillTurn: async (start, values, label, onAccepted) => {
      const errors = await streamTurn({ ...start, values }, `▶ ${label}`, onAccepted);
      if (errors) return errors;
      // De server schreef de echte gemarkeerde vraag en de actiekaarten; haal het
      // gesprek op zoals het nu is opgeslagen.
      await get().loadSession(get().activeSessionId);
      return undefined;
    },

    confirmAction: (actionRunId) => actOn(actionRunId, "confirm"),
    cancelAction: (actionRunId) => actOn(actionRunId, "cancel"),

    stop: () => controller?.abort(),

    renameSession: async (sessionId: number, title: string) => {
      const { workspaceId } = get();
      if (!workspaceId) return;
      const response = await fetch(
        `/api/workspaces/${workspaceId}/chat/sessions/${sessionId}`,
        json("PATCH", { title })
      );
      if (response.ok) get().loadSessions();
    },

    createReportPdf: async (reportId: number) => {
      const { workspaceId } = get();
      if (!workspaceId) return false;
      // 202: het maken gebeurt in een worker; het antwoord is het voorstel met
      // pdfStatus "queued". De kaart pollt daarna via refreshReport.
      const response = await fetch(
        `/api/workspaces/${workspaceId}/chat/reports/${reportId}/pdf`,
        json("POST")
      );
      if (!response.ok) return false;
      get().applyReport(await response.json());
      return true;
    },

    refreshReport: async (reportId: number) => {
      const { workspaceId } = get();
      if (!workspaceId) return undefined;
      const response = await fetch(`/api/workspaces/${workspaceId}/chat/reports/${reportId}`);
      if (!response.ok) return undefined;
      const updated: ReportProposal = await response.json();
      get().applyReport(updated);
      return updated;
    },

    // Het voorstel zit in één van de berichten; daar de nieuwe status en link bijzetten.
    applyReport: (updated: ReportProposal) => {
      set((state) => ({
        messages: state.messages.map((message) => ({
          ...message,
          reportProposals: (message.reportProposals ?? []).map((proposal) =>
            proposal.id === updated.id ? { ...proposal, ...updated } : proposal
          ),
        })),
      }));
    },

    deleteSession: async (sessionId: number) => {
      const { workspaceId } = get();
      if (!workspaceId) return;
      const response = await fetch(
        `/api/workspaces/${workspaceId}/chat/sessions/${sessionId}`,
        json("DELETE")
      );
      if (!response.ok) return;
      if (get().activeSessionId === sessionId) get().newSession();
      get().loadSessions();
    },
  };
});
